import React, { useState } from "react";
import {
  Typography,
  MenuItem,
  Box,
  Paper,
  Popover,
  Button,
  alpha,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LanguageIcon from "@mui/icons-material/Language";
import colors from "../../Constants/colors";
import { useLanguages } from "../../Query/useLanguages";
import { Languages } from "../../Enums/companyStatus";
import { useTranslation } from "react-i18next";
import { getFlagPath } from "../../Helpers/languageHelpers";

interface LanguageSelectProps {
  languageId: number;
  onLanguageChange: (id: number) => void;
  onReset: () => void;
}

const LanguageSelect: React.FC<LanguageSelectProps> = ({
  languageId,
  onLanguageChange,
}) => {
  const [selectedLanguageID, setSelectedLanguageID] =
    useState<number>(languageId);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { t } = useTranslation("evaluationPage");
  const { data: languages } = useLanguages();

  const selectedLanguage = languages?.find(
    (lang) => lang.language_id === selectedLanguageID
  );
  const flagSrc = getFlagPath(selectedLanguageID);

  const open = Boolean(anchorEl);

  const handleToggleDropdown = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const handleSelectChange = (id: number) => {
    setSelectedLanguageID(id);
    setAnchorEl(null);
    onLanguageChange(id);
  };

  const isRecommended =
    selectedLanguageID === Languages.ENGLISH ||
    selectedLanguageID === Languages.GERMAN;

  return (
    <Box
      sx={{
        mt: 3,
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        gap: 2,
      }}
    >
      {/* Language Selection Section */}
      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          flex: 1,
          borderRadius: 2,
          border: `1px solid ${colors.gray200}`,
          borderLeft: `4px solid ${colors.primary_dark}`,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            mb: 1.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <LanguageIcon sx={{ color: colors.accent, fontSize: "1.1rem" }} />
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                color: colors.primary_dark,
                fontSize: "0.875rem",
              }}
            >
              {t("evaluationPage.languageLabel")}
            </Typography>
          </Box>
        </Box>

        <Button
          onClick={handleToggleDropdown}
          variant="outlined"
          startIcon={
            flagSrc ? (
              <img
                src={flagSrc}
                alt={selectedLanguage?.language_name || "Language"}
                width={20}
                height={20}
                style={{
                  display: "block",
                  borderRadius: "4px",
                  objectFit: "cover",
                }}
              />
            ) : (
              <LanguageIcon sx={{ fontSize: "1.1rem" }} />
            )
          }
          endIcon={<ExpandMoreIcon />}
          sx={{
            width: "100%",
            justifyContent: "space-between",
            px: 1.5,
            py: 1,
            borderRadius: 2,
            borderColor: colors.gray200,
            color: colors.primary_dark,
            textTransform: "none",
            fontWeight: 500,
            fontSize: "0.875rem",
            "&:hover": {
              borderColor: colors.accent,
              backgroundColor: alpha(colors.accent, 0.2),
            },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1 }}>
            <Typography
              variant="body2"
              sx={{
                color: colors.primary_dark,
                fontWeight: 500,
                fontSize: "0.875rem",
              }}
            >
              {selectedLanguage?.language_name ||
                t("evaluationPage.selectLanguage")}
            </Typography>
            {isRecommended && (
              <Typography
                variant="caption"
                sx={{
                  color: colors.gray600,
                  fontSize: "0.7rem",
                  fontStyle: "italic",
                }}
              >
                {t("evaluationPage.recommendedSuffix")}
              </Typography>
            )}
          </Box>
        </Button>

        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          PaperProps={{
            sx: {
              mt: 1,
              border: `1px solid ${colors.gray200}`,
              borderRadius: 2,
              width: anchorEl?.clientWidth,
              maxHeight: 300,
              overflow: "auto",
              bgcolor: colors.white,
              "& .MuiMenuItem-root:hover": {
                bgcolor: colors.gray50,
              },
            },
          }}
        >
          {languages?.map((lang) => {
            const langFlagSrc = getFlagPath(lang.language_id);
            const isLangRecommended =
              lang.language_id === Languages.ENGLISH ||
              lang.language_id === Languages.GERMAN;
            return (
              <MenuItem
                key={lang.language_id}
                onClick={() => handleSelectChange(lang.language_id)}
                selected={lang.language_id === selectedLanguageID}
                sx={{
                  color: colors.gray700,
                  py: 1.25,
                  bgcolor: colors.white,
                  "&:hover": {
                    bgcolor: colors.gray50,
                  },
                  "&.Mui-selected": {
                    bgcolor: alpha(colors.accent, 0.2),
                    color: colors.primary_dark,
                    "&:hover": {
                      bgcolor: alpha(colors.accent, 0.2),
                    },
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.25,
                    width: "100%",
                  }}
                >
                  {langFlagSrc && (
                    <img
                      src={langFlagSrc}
                      alt={lang.language_name}
                      width={20}
                      height={20}
                      style={{
                        display: "block",
                        borderRadius: "4px",
                        objectFit: "cover",
                      }}
                    />
                  )}
                  <Typography
                    variant="body2"
                    sx={{
                      flex: 1,
                      fontWeight:
                        lang.language_id === selectedLanguageID ? 600 : 400,
                      color: "inherit",
                      fontSize: "0.875rem",
                    }}
                  >
                    {lang.language_name}
                  </Typography>
                  {isLangRecommended && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: colors.gray500,
                        fontSize: "0.7rem",
                        fontStyle: "italic",
                      }}
                    >
                      {t("evaluationPage.recommendedSuffix")}
                    </Typography>
                  )}
                </Box>
              </MenuItem>
            );
          })}
        </Popover>
      </Paper>

    </Box>
  );
};

export default LanguageSelect;
